import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { ApplicationConfig, ErrorHandler, importProvidersFrom } from "@angular/core";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { provideRouter, Routes, withEnabledBlockingInitialNavigation, withInMemoryScrolling } from "@angular/router";
import { NgIdleKeepaliveModule } from "@ng-idle/keepalive";
import { definePreset } from "@primeng/themes";
import Lara from "@primeng/themes/lara";
import { MarkdownModule } from "ngx-markdown";
import { provideToastr, ToastrModule } from "ngx-toastr";
import { providePrimeNG } from "primeng/config";
import { AuthHttpInterceptor } from "./auth-http-interceptor";
import { SentryHttpInterceptor } from "./sentry-http-interceptor";
import { GlobalErrorHandler } from "./shared/global-error-handler";
import { SharedModule } from "./shared/shared.module";

import { routes as adminRoutes } from "./admin/admin.module";
import { routes as homeRoutes } from "./home/home.module";
import { routes as inventoryRoutes } from "./inventory/inventory.module";
import { routes as jobOrdersRoutes } from "./job-orders/job-orders.module";
import { routes as loginRoutes } from "./login/login.module";
import { routes as notificationRoutes } from "./notifications/notifications.module";
import { routes as purchasingRoutes } from "./purchasing/purchasing.module";
import { routes as scannerRoutes } from "./scanner/scanner.module";
import { routes as documentationRoutes } from "./documentation/documentation.module";
import { routes as handheldRoutes } from "./handheld/handheld.module";
import { routes as notepadRoutes } from "./notepad/notepad.module";
import { routes as orderFacilitationRoutes } from "./order-facilitation/order-facilitation.module";

const appRoutes: Routes = [
  ...homeRoutes,
  ...adminRoutes,
  ...loginRoutes,
  ...inventoryRoutes,
  ...jobOrdersRoutes,
  ...scannerRoutes,
  ...purchasingRoutes,
  ...notificationRoutes,
  ...handheldRoutes,
  ...notepadRoutes,
  ...documentationRoutes,
  ...orderFacilitationRoutes,
  {
    path: "**",
    redirectTo: "home",
  },
];

const MyPreset = definePreset(Lara, {
  semantic: {
    primary: {
      50: "{blue.50}",
      100: "{blue.100}",
      200: "{blue.200}",
      300: "{blue.300}",
      400: "{blue.400}",
      500: "{blue.500}",
      600: "{blue.600}",
      700: "{blue.700}",
      800: "{blue.800}",
      900: "{blue.900}",
      950: "{blue.950}",
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      appRoutes,
      withInMemoryScrolling({
        anchorScrolling: "enabled",
        scrollPositionRestoration: "enabled",
      }),
      withEnabledBlockingInitialNavigation()
    ),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: { preset: MyPreset, options: { darkModeSelector: ".app-dark" } },
    }),
    provideToastr(),

    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SentryHttpInterceptor,
      multi: true,
    },

    importProvidersFrom(MarkdownModule.forRoot()),
    importProvidersFrom(SharedModule),
    importProvidersFrom(NgIdleKeepaliveModule.forRoot()),
    importProvidersFrom(
      ToastrModule.forRoot({
        progressBar: true,
        progressAnimation: "increasing",
        enableHtml: true,
      })
    ),
  ],
};
